import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { login, register } from '../../store/thunks/authThunks';
import { clearError } from '../../store/slices/authSlice';

const LoginModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, error } = useSelector(state => state.auth);
  
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Закрываем модальное окно при успешной аутентификации
  useEffect(() => {
    if (isAuthenticated && open) {
      console.log('User authenticated, closing modal');
      handleClose();
    }
  }, [isAuthenticated, open]);

  const handleClose = () => {
    // Сбрасываем состояние формы
    setFormData({
      email: '',
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setActiveTab(0);
    setShowPassword(false);
    setShowConfirmPassword(false);
    dispatch(clearError());
    
    // Вызываем колбэк закрытия
    if (onClose) {
      onClose();
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Некорректный формат email';
    }

    if (!formData.password) {
      errors.password = 'Пароль обязателен';
    } else if (formData.password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов';
    }

    if (activeTab === 1 && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const result = await dispatch(login({
      email: formData.email,
      password: formData.password
    }));

    if (result.type === 'auth/login/fulfilled') {
      // Закрытие произойдет автоматически через useEffect
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Используем существующую функцию register вместо registerViaAdmin
    const result = await dispatch(register({
      email: formData.email,
      password: formData.password,
      traits: {
        name: formData.email.split('@')[0] // Генерируем имя из email
      }
    }));

    if (result.type === 'auth/register/fulfilled') {
      setSuccessMessage('Регистрация прошла успешно! Вы автоматически вошли в систему.');
      // Модальное окно закроется автоматически через useEffect
    } else if (result.type === 'auth/register/rejected') {
      // Ошибка уже обработана в thunk
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (error || successMessage) {
      dispatch(clearError());
      setSuccessMessage('');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFormErrors({});
    setFormData({
      email: '',
      password: '',
      confirmPassword: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    dispatch(clearError());
    setSuccessMessage('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }
      }}
    >
      {/* Заголовок с кнопкой закрытия */}
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          SIM - UEF
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 3 }}>
          {activeTab === 0 ? 'Вход в систему' : 'Создание аккаунта'}
        </Typography>

        {/* Сообщения об ошибках и успехе */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Табы */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ 
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: 'bold'
            }
          }}
        >
          <Tab label="Вход" />
          <Tab label="Регистрация" />
        </Tabs>

        {/* Форма входа */}
        {activeTab === 0 ? (
          <form onSubmit={handleLogin}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="login-email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Пароль"
              type={showPassword ? 'text' : 'password'}
              id="login-password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
          </form>
        ) : (
          /* Форма регистрации */
          <form onSubmit={handleRegister}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="register-email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Пароль"
              type={showPassword ? 'text' : 'password'}
              id="register-password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Подтвердите пароль"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirm-password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={toggleConfirmPasswordVisibility}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 2 }}>
        {/* Кнопки действий */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          onClick={activeTab === 0 ? handleLogin : handleRegister}
          size="large"
          sx={{ 
            py: 1.5,
            fontWeight: 'bold',
            fontSize: '1rem'
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : activeTab === 0 ? (
            'Войти'
          ) : (
            'Зарегистрироваться'
          )}
        </Button>

        {/* Ссылка для переключения между вкладками */}
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Link 
            component="button" 
            type="button"
            variant="body2" 
            onClick={() => setActiveTab(activeTab === 0 ? 1 : 0)}
            sx={{ 
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {activeTab === 0 ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
          </Link>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default LoginModal;